import type { SetupImportPreview } from '@mss_creator/shared';
import { frenchList, setupFieldLabel } from './setup-import-messages';

export interface SetupImportWarningsProps {
  preview: SetupImportPreview;
}

/**
 * « À savoir avant d'appliquer » (board 03): everything the import leaves
 * untouched, so nothing kept or skipped goes unnoticed. Renders nothing when
 * there is nothing to say.
 */
export function SetupImportWarnings({ preview }: SetupImportWarningsProps) {
  const { missingColumns, ignoredColumns, missingSteps, skippedTooLong } = preview;
  if (
    missingColumns.length === 0 &&
    ignoredColumns.length === 0 &&
    missingSteps.length === 0 &&
    skippedTooLong.length === 0
  ) {
    return null;
  }
  const missingLabels = frenchList(missingColumns.map(setupFieldLabel));
  const stepLabels = frenchList(missingSteps.map(String));

  return (
    <div
      className="mb-3 rounded-md border-2 border-jaune bg-jaune-tint px-3 py-2 text-small"
      data-testid="setup-import-warnings"
    >
      <h3 className="mb-1 font-bold text-jaune-deep text-small">À savoir avant d'appliquer</h3>
      <ul className="list-disc space-y-0 pl-4">
        {missingColumns.length === 1 && (
          <li>
            Colonne <strong>{missingLabels}</strong> absente : valeurs actuelles conservées.
          </li>
        )}
        {missingColumns.length > 1 && (
          <li>
            Colonnes <strong>{missingLabels}</strong> absentes : valeurs actuelles conservées.
          </li>
        )}
        {ignoredColumns.map((header) => (
          <li key={header}>
            Colonne <strong>{header}</strong> non reconnue : ignorée.
          </li>
        ))}
        {missingSteps.length === 1 && (
          <li>
            <strong>Étape {stepLabels}</strong> absente de l'import : inchangée.
          </li>
        )}
        {missingSteps.length > 1 && (
          <li>
            <strong>Étapes {stepLabels}</strong> absentes de l'import : inchangées.
          </li>
        )}
        {skippedTooLong.map((skip) => (
          <li key={`${skip.order}:${skip.field}`}>
            <strong>
              Étape {skip.order} - {setupFieldLabel(skip.field)}
            </strong>{' '}
            : {skip.length} / {skip.limit} caractères, non importé. Raccourcissez-le puis
            réimportez, ou saisissez-le dans le tableau.
            <details className="mt-1">
              <summary className="cursor-pointer text-caption text-gray-500">Voir le texte</summary>
              <span className="whitespace-pre-wrap text-caption text-gray-500">{skip.text}</span>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
